import dayjs from "dayjs";
import classNames from "classnames";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { batch, useDispatch, useSelector } from "react-redux";
import { Week } from "../Week";
import WeekSkeuomorphism from "../Week/skeuomorphism";
import { DateSwitcher } from "../../utility/DateSwitcher"
import CalendarLayout from "../Calendar/Calendar";
import { Settings } from "../../Settings";
import { setTheme, setShowPastDays, setThemeColor, setEmail, setEventsToDelete, setDesign, setNav } from "../../../store/settings";
import { deleteEventsAPI, getProfileDataAPI, getCategoriesAPI, setLayoutAPI } from "../../../api";
import { fetchedCategories } from "../../../store/categories";
import { openModal } from "../../../store/modal";
import { setLoading } from "../../../store/auth";
import { setLayout, setAllDates, setAnchor as setAnchorMonth, setViewYear } from "../../../store/calendar";
import { Spinner } from "../../utility/Spinner";
import { setEventDeletion } from "../../../store/settings";
import { eventsMerged, removeEvents } from "../../../store/events";
import { fetchThematics } from "../../../store/thematics";
import IconSettings from "./icons/icon-settings.svg"
import BurgerSettings from "../../../icons/redesign/settings.svg"
import { getUser, setAnchorAPI } from "../../../api/profileAPI";
import { YEAR, navFromProfile, openingDates, profileAnchor } from "../../../functions/calendarNav";
import { getThematics } from "../../../api/thematics";
import { getBootstrapAPI } from "../../../api/bootstrapAPI";
import { preloadGridLayout } from "../GridLayout/gridLayout";
import { preloadEmojis } from "../../../emojis/emojiData";
import { DayAsWindow } from "../Day/DayAsWindow";
import { useEventsRange, visibleRange } from "../../../store/eventsLoader";
import './styles.scss'

function CalendarApp() {
    const dispatch = useDispatch();
    const layoutDates = [
        useSelector(state => state.calendar.dayDate),
        useSelector(state => state.calendar.weekDate),
        useSelector(state => state.calendar.monthDate),
        useSelector(state => state.calendar.halfYearDate),
        useSelector(state => state.calendar.yearDate),
        useSelector(state => state.calendar.quarterYearDate),
    ];
    const themeColor = useSelector(state => state.settings.themeColor);
    const layout = useSelector(state => state.calendar.layout);
    const isThematicsLayout = useSelector(state => state.settings.isThematicsLayout)
    const design = useSelector(state => state.settings.design)
    const userId = useSelector(state => state.auth.user);
    const eventDeletion = useSelector(state => state.settings.eventDeletion)
    const eventsToDelete = useSelector(state => state.settings.eventsToDelete)
    const [isDeletionConfirmed, setIsDeletionConfirmed] = useState(false)
    // const dayDate = useSelector(state => state.calendar.dayDate);
    // const weekDate = useSelector(state => state.calendar.weekDate);
    // const monthDate = useSelector(state => state.calendar.monthDate);
    // const halfYearDate = useSelector(state => state.calendar.halfYearDate);
    // const yearDate = useSelector(state => state.calendar.yearDate);
    const loading = useSelector(state => state.auth.loading);
    const layouts = useMemo(() => [
        { label: 'day', period: 1 },
        { label: 'week', period: 7 },
        { label: 'month', period: 1 },
        { label: 'half-year', period: 6 },
        { label: 'year', period: 12 },
        { label: 'quarter-year', period: 3 },
    ], []);
    const initialDate = useMemo(() => layoutDates[layout], [layoutDates, layout]);
    // The startup request brings the first view's events, so the month loader waits for it.
    const [bootstrapped, setBootstrapped] = useState(false);
    const [rangeFrom, rangeTo] = visibleRange(layout, initialDate);
    useEventsRange(bootstrapped ? rangeFrom : null, bootstrapped ? rangeTo : null);
    
    const [displaySettings, setDisplaySettings] = useState(false);
    const setSelectedDate = useCallback((date) => {
        dispatch(openModal({
            date: date,
            updateID: null,
            isNew: false
        }));
    }, [dispatch]);


    const cancelEventDeletion = useCallback(() => {
        dispatch(setEventDeletion(false))
        dispatch(setEventsToDelete([]))
    }, [dispatch]);

    const alertEventDeletion = useCallback(() => {
        setIsDeletionConfirmed(true)
    }, []);

    const deleteSelectedEvents = useCallback(() => {
        if (eventsToDelete.length === 0) return;
        deleteEventsAPI(eventsToDelete)
            .then(
                res => {
                    dispatch(removeEvents(eventsToDelete));
                }
            )
            .catch(err => console.log("ERROR", err));
        cancelEventDeletion();
    }, [eventsToDelete, dispatch, cancelEventDeletion]);

    // The month the calendar is anchored at, kept in the profile as its custom first month and year so that the
    // calendar opens there next time; null follows today. Arrows save it once they stop, the rest at once, and
    // nothing is sent for the month already saved.
    const savedAnchor = useRef(null);
    const anchorTimer = useRef(null);
    const setAnchor = useCallback((month, later = false) => {
        dispatch(setAnchorMonth(month));
        clearTimeout(anchorTimer.current);
        const key = month ? month.format('YYYY-MM') : null;
        if (key === savedAnchor.current) return;
        const save = () => {
            savedAnchor.current = key;
            setAnchorAPI(month).catch(err => console.log("ERROR", err));
        };
        if (later) anchorTimer.current = setTimeout(save, 800);
        else save();
    }, [dispatch]);

    // A month's heading opens the month and anchors the calendar there.
    const onMonthClick = (layout, month, year) => {
        const start = dayjs(`${year}-${month + 1}-01`, "YYYY-MM-DD");
        setAnchor(start);
        setView(start, layout);
    }

    const applyProfile = (profileData) => {
        dispatch(setTheme(profileData.theme));
        dispatch(setThemeColor(profileData.theme_color));
        dispatch(setDesign(profileData.design ? profileData.design : 'Original'));
        dispatch(setShowPastDays(profileData.past_days));
        dispatch(setNav(navFromProfile(profileData.nav)));
        // Where the calendar was left: the anchored month, else today. A January anchor is the year key's year.
        const anchor = profileAnchor(profileData);
        savedAnchor.current = anchor ? anchor.format('YYYY-MM') : null;
        dispatch(setAnchorMonth(anchor));
        dispatch(setAllDates(openingDates(anchor)));
        if (anchor && anchor.month() === 0 && profileData.current_view === YEAR) dispatch(setViewYear(anchor.year()));

        dispatch(setLayout(profileData.current_view));
        dispatch(setLoading(false));
    }

    // The old way to open: the parts one by one, with the month loader fetching events. Used when the API
    // has no /bootstrap yet.
    const loadSeparately = () => {
        getProfileDataAPI(localStorage.getItem("user"))
            .then(applyProfile)
            .catch(err => console.log(err));

        getUser().then(res => {
            if (res.status === 200 && res.data.email) {
                dispatch(setEmail(res.data.email))
            }
        }).catch(err => console.log(err));

        getCategoriesAPI().then(res => {
            dispatch(fetchedCategories(res.data));
        }).catch(err => console.log(err));

        getThematics().then(res => {
            if (res.status === 200) {
                dispatch(fetchThematics(res.data))
            }
        }).catch(err => console.log(err));
    }

    useEffect(() => {
        // A response that arrives after unmount is dropped. React's StrictMode mounts twice in development,
        // so only the second request applies there.
        let cancelled = false;
        dispatch(setLoading(true));
        getBootstrapAPI()
            .then(data => {
                if (cancelled) return;
                // One render: the data goes in first, and the profile (which ends loading) goes in last.
                batch(() => {
                    if (data.user.email) {
                        dispatch(setEmail(data.user.email));
                    }
                    dispatch(fetchedCategories(data.categories));
                    dispatch(fetchThematics(data.thematics));
                    dispatch(eventsMerged({ events: data.events, months: data.months }));
                    applyProfile(data.profile);
                });
            })
            .catch(err => {
                if (cancelled) return;
                console.log(err);
                loadSeparately();
            })
            .finally(() => {
                if (cancelled) return;
                setBootstrapped(true);
                // After the calendar's data, so that on a slow connection it doesn't compete with it.
                preloadGridLayout();
                preloadEmojis();
            });
        return () => { cancelled = true; };
    }, []);

    const saveInitialDateToStore = useCallback((date, layout) => {
        dispatch(setAllDates({
            dayDate: date,
            weekDate: date,
            monthDate: date,
            halfYearDate: date,
            yearDate: date,
            quarterYearDate: date
        }));
    }, [dispatch]);

    const saveLayout = useCallback((layout) => {
        setLayoutAPI(userId, layout)
            .then(res => dispatch(setLayout(res.data.current_view)))
            .catch(err => console.log(err));
    }, [userId, dispatch]);

    // A view of the bar opens at once, with its date, while the profile saves it.
    const setView = useCallback((date, layout) => {
        batch(() => {
            saveInitialDateToStore(date, layout);
            dispatch(setLayout(layout));
        });
        saveLayout(layout);
    }, [saveInitialDateToStore, saveLayout, dispatch]);

    // A week number opens its week, at once too.
    const layoutOnClick = setView;

    return loading ? (
        <Spinner />
    ) : (
        <div className={classNames({
            "calendar": true,
            [design]: true,
        })}>
            <Settings
                display={displaySettings}
                close={() => setDisplaySettings(false)}
            />
            <div className={classNames({
                'panel': true,
            })}>
                
                <DateSwitcher type={layouts[layout].label} date={initialDate} setDate={saveInitialDateToStore} setView={setView} setAnchor={setAnchor} />
                <div className="settings-switcher-container">
                    {eventDeletion &&
                        <div className="delete-events-settings">
                            {(!isDeletionConfirmed && eventsToDelete.length > 0) &&
                                <button type="button" className="btn-event-delete-confirm" onClick={alertEventDeletion}>
                                    <i className={classNames({
                                        "icon-bin-gray": true,
                                    })} />
                                    <div>Удалить {eventsToDelete.length > 0 && <span dangerouslySetInnerHTML={{ __html: `(` + eventsToDelete.length + `)` }}></span>}</div>
                                </button>
                            }
                            {isDeletionConfirmed &&
                                <button type="button" className="btn-event-delete" onClick={deleteSelectedEvents} >
                                    <i className={classNames({
                                        "icon-bin": true,
                                    })} />
                                    Да, удалить
                                </button>
                            }

                            <button type="button" className="btn-event-delete-cancel" onClick={cancelEventDeletion}>
                                Отменить
                            </button>


                        </div>
                    }
                    <span className={
                        classNames({ 'settings-icon': true, 'blue': themeColor === 'blue' })
                    } onClick={() => setDisplaySettings(true)}><img src={design == 'Skeuomorphism' ? BurgerSettings : IconSettings} alt="settings" /></span>
                </div>
            </div>
            {layout >= 2 && <CalendarLayout
                initialDate={initialDate}
                layoutOnClick={layoutOnClick}
                onMonthClick={onMonthClick}
                period={layouts[layout].period}
                setSelectedDate={(date) => setSelectedDate(date)} />}
            {layout === 1 && design === 'Skeuomorphism' &&
                <WeekSkeuomorphism
                    initialDate={initialDate}
                    layoutOnClick={layoutOnClick}
                    onMonthClick={onMonthClick}
                    period={layouts[layout].period}
                    setSelectedDate={(date) => setSelectedDate(date)} />}
            {layout === 1 && design === 'Original' &&
                <Week date={initialDate} />
            }
            {layout === 0 && <div className="day-container"><DayAsWindow date={initialDate} /></div>}


        </div >
    )
}

export { CalendarApp };